import type { Payment } from '@aidkitorg/aidkit/lib/admin_panel/payments';
import { Badge, Button, Card, CardBody, DataTable } from '@aidkitorg/component-library';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePayments } from '../../../Contexts/PaymentsContext';
import { useVisibilityPolling } from '../../../Hooks/VisibilityPolling';
import { useLocalizedStrings } from '../../../Localization';
import { Filters, PaymentsFilter } from './PaymentsFilter';
import type { PaymentsOverview } from '@aidkitorg/types/lib/survey';

const getMaxBatchTotalAmount = (payments: Payment[]): number => {
  return payments.reduce((max, payment) => {
    return payment.totalAmount > max ? payment.totalAmount : max
  }, 0)
}

export const PaymentsDataTable: React.FC<{
  lastUpdatedTimestamp?: number
  pageConfig?: PaymentsOverview
  isConfigLoading: boolean
}> = ({
  lastUpdatedTimestamp,
  pageConfig,
  isConfigLoading
}) => {
  const L = useLocalizedStrings()

  const PAYMENT_COLUMNS: ColumnDef<Payment>[] = [
    {
      accessorKey: 'batchName',
      header: L.admin_panel.payments.batch_name,
      cell: ({ row }) => {
        const targetField = row.original.targetField
        return (
          <Button 
            className="!text-left"
            variant="link"
            onClick={(e) => {
              history.push("/admin-panel/payments/payment-details/"+targetField)
              e.preventDefault()
            }}
          >
            {row.original.batchName}
          </Button>
        )}
    },
    {
      accessorKey: "paymentEnabledStatus",
      header: L.admin_panel.payments.payment_enabled,
      cell: ({ row }) => {
        const paymentEnabledStatus = row.original.paymentEnabledStatus
        const label =  paymentEnabledStatus === 'enabled' ? L.admin_panel.payments.enabled : L.admin_panel.payments.disabled
        const variant = paymentEnabledStatus === 'enabled' ? 'success' : 'neutral'
        return <Badge variant={variant}>{label}</Badge>
      },
    },
    {
      accessorKey: "paymentEligibleRecipients",
      header: L.admin_panel.payments.payment_eligible
    },
    {
      accessorKey: "paymentInitiatedRecipients",
      header: L.admin_panel.payments.payments_initiated
    },
    {
      accessorKey: "failed",
      header: L.admin_panel.payments.failed,
      filterFn: (row, columnId, filterValue) => {
        return filterValue && row.original.failed > 0;
      }
    },
    {
      accessorKey: "totalAmount",
      header: L.admin_panel.payments.total_amount,
      cell: ({ row }) => (
        <span>${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.original.totalAmount)}</span>
      ),
      filterFn: (row, columnId, filterValue) => {
        const amount = row.getValue(columnId) as number
        const min = filterValue[0]
        const max = filterValue[1]
  
        return amount >= min && amount <= max
      }
    },
    // Commenting this out for now as we figure out how/whether to tie notifiations and payments together
    // {
    //   accessorKey: "notifications",
    //   header: "Notifications",
    //   cell: ({ row }) => {
    //     const status = row.original.notifications
    //     const label = status === 'enabled' ? "Enabled" : "Disabled"
    //     const variant = status === 'enabled' ? 'success' : 'neutral'
    //     return <Badge variant={variant}>{label}</Badge>
    //   },
    // },
    {
      accessorKey: "firstPaymentDate",
      header: L.admin_panel.payments.first_payment,
      cell: ({ row }) => {
        if (row.original.firstPaymentDate == '') return "--"
        const date = new Date(row.original.firstPaymentDate)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)
  
        return <span>{formatted}</span>
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.firstPaymentDate).getTime()
        const b = new Date(rowB.original.firstPaymentDate).getTime()
        return a - b
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue?.firstPaymentStart || !filterValue?.firstPaymentEnd) return true
        const date = row.getValue(columnId) as Date
        return (
          date >= new Date(filterValue.firstPaymentStart) &&
          date <= new Date(filterValue.firstPaymentEnd)
        )
      },
    },
    {
      accessorKey: "lastPaymentDate",
      header: L.admin_panel.payments.last_payment,
      cell: ({ row }) => {
        if (row.original.lastPaymentDate == '') return "--"
        
        const date = new Date(row.original.lastPaymentDate)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)
  
        return <span>{formatted}</span>
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.firstPaymentDate).getTime()
        const b = new Date(rowB.original.firstPaymentDate).getTime()
        return a - b
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue?.lastPaymentStart || !filterValue?.lastPaymentEnd) return true
        const date = row.getValue(columnId) as Date
        return (
          date >= new Date(filterValue.lastPaymentStart) &&
          date <= new Date(filterValue.lastPaymentEnd)
        )
      },
    },
  ]
  
  const { payments,  fetchPaymentsOverview, isPaymentsLoading} = usePayments()
  const [maxBatchTotalAmount, setMaxBatchTotalAmount] = useState<number>(0)
  const [filters, setFilters] = useState<Filters>({
    paymentEnabledStatus: "",
    // notifications: "",
    firstPaymentStart: "",
    firstPaymentEnd: "",
    lastPaymentStart: "",
    lastPaymentEnd: "",
    hasFailedPayments: false,
    batchTotalAmountRange: [0, maxBatchTotalAmount]
  })
  const [activeFilters, setActiveFilters] = useState<number>(0)  
  const history = useHistory()

  const location = useLocation()
  const isOnPaymentsPage = location.pathname === '/admin-panel/payments'

  const paymentActions: TableAction<Payment>[] = [
    {
      label: L.admin_panel.payments.view_recipients,
      onClick: (row: Payment ) => {
        const targetField = row.targetField
        history.push('/admin-panel/payments/payment-details/'+targetField)
      },
      icon: 'UserGroupIcon'
    },
  ]

  // Determine which columns to show based on pageConfig
  const visibleColumns = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, show nothing
      return [];
    }

    const config = pageConfig.columns;
    return PAYMENT_COLUMNS.filter((column) => {
      // Only filter columns that have an accessorKey
      if (!('accessorKey' in column)) return true;

      const key = column.accessorKey as string;

      // Always show batchName column
      if (key === 'batchName') return true;

      // Check config for other columns
      if (key === 'paymentEnabledStatus') return config.paymentStatus !== 'hidden';
      if (key === 'paymentEligibleRecipients') return config.eligible !== 'hidden';
      if (key === 'failed') return config.failed !== 'hidden';
      if (key === 'totalAmount') return config.total !== 'hidden';
      if (key === 'firstPaymentDate') return config.firstPaymentDate !== 'hidden';
      if (key === 'lastPaymentDate') return config.lastPaymentDate !== 'hidden';
      if (key === 'paymentInitiatedRecipients') return true; // Always show initiated

      // Default: show column if not explicitly configured
      return true;
    });
  }, [PAYMENT_COLUMNS, pageConfig]);

  // Determine if search should be enabled
  const enableSearch = pageConfig?.searchBar !== 'hidden';

  // Determine if there are any available filters based on pageConfig
  const hasAvailableFilters = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, all filters are available by default
      return true;
    }

    const columns = pageConfig.columns;
    // Check if at least one filterable column is visibleAndFilterable
    return columns.paymentStatus === 'visibleAndFilterable' ||
           columns.failed === 'visibleAndFilterable' ||
           columns.total === 'visibleAndFilterable' ||
           columns.firstPaymentDate === 'visibleAndFilterable' ||
           columns.lastPaymentDate === 'visibleAndFilterable';
  }, [pageConfig]);

  useVisibilityPolling({
    onImmediate: () => fetchPaymentsOverview(),
    onInterval: () => fetchPaymentsOverview(),
    isActive: isOnPaymentsPage,
    intervalMs: 2 * 60 * 1000
  })

  useEffect(() => {
    setMaxBatchTotalAmount(getMaxBatchTotalAmount(payments))
  }, [payments])

  useEffect(() => {
    setFilters(f => ({... f, batchTotalAmountRange: [0, maxBatchTotalAmount]}))
  }, [maxBatchTotalAmount])
  
  const paymentsEmpty = !isPaymentsLoading && payments.length === 0
  const paymentsEmptyState = (
    <div className="py-2 text-center text-gray-600">
      {L.admin_panel.payments.no_payments_configured}
    </div>
  )

  return (
    <div className="flex flex-col gap-y-1">
      <Card className="rounded-md">
        <CardBody>
          {paymentsEmpty ? paymentsEmptyState : (
            <DataTable
              columns={visibleColumns}
              data={payments}
              actions={paymentActions}
              enableSearch={enableSearch}
              searchLabel={L.admin_panel.payments.search_payments}
              filters={hasAvailableFilters ? (table) => (
                <PaymentsFilter
                  filters={filters}
                  setFilters={setFilters}
                  maxBatchTotalAmount={maxBatchTotalAmount}
                  table={table}
                  setActiveFilters={setActiveFilters}
                  defaultTableColumns={pageConfig?.columns}
                />
              ) : undefined}
              activeFilters={activeFilters}
              dataFilteredOutMessage={L.admin_panel.payments.no_payments_match_filter}
              loading={isPaymentsLoading || isConfigLoading}
              showPagination={true}
              lastUpdatedTimestamp={lastUpdatedTimestamp}
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}